// src/pages/processifyd/ProcessifydClients.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faList
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydClients = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [loading, setLoading] = useState(false);

  const initialForm = {
    clientName: "",
    briefAboutClient: "",
    clientAddress: "",
    clientCity: "",
    clientCountry: "India",
    postalCode: "",
    clientEmailID: "",
    clientContactNumber: "",
    active: "Yes",
    archive: "No",
  };

  const [formData, setFormData] = useState(initialForm);

  // Sample Demo Data (as per your Excel wireframe)
  const demoClients = [
    {
      id: 1,
      clientName: "21st Century Financial Services",
      briefAboutClient: "21st Century Financial Services, founded in 2001 is a Mumbai based Financial Institution. The institution that plays a significant role in the financial sector in India, focusing on various loan products and services. It aspires to grow further.",
      clientAddress: "Fort",
      clientCity: "Mumbai",
      clientCountry: "India",
      postalCode: "400001",
      clientEmailID: "contact@twentyfirstcenturybank.com",
      clientContactNumber: "+91 22 1234567",
      active: "Yes",
      archive: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
    {
      id: 2,
      clientName: "TechNova Solutions Pvt Ltd",
      briefAboutClient: "Leading software development company specializing in fintech and cloud solutions.",
      clientAddress: "Bandra Kurla Complex",
      clientCity: "Mumbai",
      clientCountry: "India",
      postalCode: "400051",
      clientEmailID: "info@technova.in",
      clientContactNumber: "+91 22 98765432",
      active: "Yes",
      archive: "No",
      createdBy: "Sanjeev Kumar",
      createdDate: "15/01/24 10:15",
      lastUpdatedBy: "Priya Sharma",
      lastUpdateDate: "10/03/24 14:20",
    },
  ];

  const loadSample = () => {
    setClients(demoClients);
  };

  const handleAddNew = () => {
    setEditingClient(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      clientName: client.clientName,
      briefAboutClient: client.briefAboutClient || "",
      clientAddress: client.clientAddress || "",
      clientCity: client.clientCity || "",
      clientCountry: client.clientCountry || "India",
      postalCode: client.postalCode || "",
      clientEmailID: client.clientEmailID || "",
      clientContactNumber: client.clientContactNumber || "",
      active: client.active || "Yes",
      archive: client.archive || "No",
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this client? This action cannot be undone.")) return;
    setClients((prev) => prev.filter((c) => c.id !== id));
  };

  const handleOpenClient = (client) => {
    // Change this route as per your actual client detail page
    navigate(`/processifyd-clienthub/${client.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.clientName.trim() || !formData.clientEmailID.trim()) {
      alert("Client Name and Email ID are required.");
      return;
    }

    const newClient = {
      id: editingClient?.id || Date.now(),
      ...formData,
      createdBy: editingClient?.createdBy || "Sanjeev Kumar",
      createdDate: editingClient?.createdDate || new Date().toLocaleString("en-IN"),
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: new Date().toLocaleString("en-IN"),
    };

    if (editingClient) {
      setClients((prev) =>
        prev.map((c) => (c.id === editingClient.id ? newClient : c))
      );
    } else {
      setClients((prev) => [...prev, newClient]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // Filter + Pagination
  const filtered = clients.filter(
    (c) =>
      c.clientName.toLowerCase().includes(search.toLowerCase()) ||
      c.clientEmailID.toLowerCase().includes(search.toLowerCase()) ||
      c.clientCity.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const [showList, setShowList] = useState(false);
  
  const handleShowMyClients = async () => {
    try {
      setLoading(true);

      // 🔄 Replace this later with API call
      // const response = await axios.get("/api/clients");
      // setClients(response.data);

      // ✅ Temporary Demo Data (remove later)
      setClients([]);

      setShowList(true);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header - Same as reference */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            📋 Processifyd
          </div>
          <div className="text-xs text-gray-400">
            Client Master Management
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

        <button
          className="md:hidden text-xl w-10 text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="w-12 px-2 py-1 bg-red-700 rounded-md">
                ⏻
              </button>
            </div>
          ) : (
            <button onClick={() => navigate("/login")} className="w-12 px-2 py-1 border border-teal-600 rounded-md">
              🔐
            </button>
          )}
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex flex-row gap-3">
            <button
              onClick={loadSample}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-48 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              Load Sample Clients
            </button>
            <button
              onClick={handleShowMyClients}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faList} />
              My Client List
            </button>
            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Client
            </button>
          </div>

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by client name, email or city..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500/60"
            />
          </div>
        </div>

        {/* Client Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((client) => (
            <div
              key={client.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300 transition-colors line-clamp-2">
                    {client.clientName}
                  </h3>
                  <div className="text-sm text-gray-400 mt-1">
                    {client.clientCity}, {client.clientCountry}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${client.active === "Yes" ? "bg-emerald-600" : "bg-red-600"}`}>
                    {client.active === "Yes" ? "Active" : "Inactive"}
                  </span>
                  {client.archive === "Yes" && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-600">Archived</span>
                  )}
                </div>
              </div>

              <p className="mt-4 text-sm text-gray-300 line-clamp-3">
                {client.briefAboutClient || "No brief available"}
              </p>

              <div className="mt-4 text-xs text-gray-500 space-y-1">
                <div><strong>Email:</strong> {client.clientEmailID}</div>
                <div><strong>Contact:</strong> {client.clientContactNumber}</div>
                {client.clientAddress && <div><strong>Address:</strong> {client.clientAddress}</div>}
              </div>

              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenClient(client)}
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  Open
                </button>

                <button
                  onClick={() => handleEdit(client)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit Client"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>

                <button
                  onClick={() => handleDelete(client.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete Client"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search ? "No clients match your search" : "No clients yet. Click 'New Client' or 'Load Sample Clients'"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {showList && (
          <div className="mt-4 bg-black text-white rounded-lg p-4">
            {loading ? (
              <p className="text-yellow-200">Loading clients...</p>
            ) : clients.length === 0 ? (
              <p className="text-yellow-200">No clients found.</p>
            ) : (
              <ul className="space-y-2">
                {clients.map((cli) => (
                  <li
                    key={cli.id}
                    className="border border-gray-700 rounded p-2"
                  >
                    <p className="font-semibold text-gray-100">{cli.clientName}</p>
                    <p className="font-semibold text-gray-100">{cli.active}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ──── Add / Edit Modal ──── */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingClient ? "Edit Client" : "New Client"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white w-12 text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm mb-1.5">Client Name <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. 21st Century Financial Services"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Brief about the Client <span className="text-red-400">*</span></label>
                  <textarea
                    required
                    value={formData.briefAboutClient}
                    onChange={(e) => setFormData({ ...formData, briefAboutClient: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="Short description about the client..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Client Address</label>
                    <input
                      value={formData.clientAddress}
                      onChange={(e) => setFormData({ ...formData, clientAddress: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. Fort, Mumbai"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Client City <span className="text-red-400">*</span></label>
                    <input
                      required
                      value={formData.clientCity}
                      onChange={(e) => setFormData({ ...formData, clientCity: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="Mumbai"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Client Country <span className="text-red-400">*</span></label>
                    <input
                      required
                      value={formData.clientCountry}
                      onChange={(e) => setFormData({ ...formData, clientCountry: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Postal Code <span className="text-red-400">*</span></label>
                    <input
                      required
                      value={formData.postalCode}
                      onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="400001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Active <span className="text-red-400">*</span></label>
                    <select
                      value={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                      className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Client Email ID <span className="text-red-400">*</span></label>
                    <input
                      required
                      type="email"
                      value={formData.clientEmailID}
                      onChange={(e) => setFormData({ ...formData, clientEmailID: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Client Contact Number</label>
                    <input
                      value={formData.clientContactNumber}
                      onChange={(e) => setFormData({ ...formData, clientContactNumber: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="+91 22 1234567"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <label className="block text-sm mb-1.5">Archive / Archived</label>
                  <select
                    value={formData.archive}
                    onChange={(e) => setFormData({ ...formData, archive: e.target.value })}
                    className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingClient ? "Update Client" : "Create Client"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer - Same as reference */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydClients;